import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import ListSheet, {
  SnapPoint,
  SnapPointType,
} from 'components/CasinoTabBar/views/home/views/MapStats/HotListReport/components/ListSheet';
import { OrderByEnum } from 'common/common';
import {
  FOOTER_VISIBILITY_EVENT,
  FooterVisibilityEventDetail,
} from 'common/footerVisibility';

interface ListSheetLeaderboardProps {
  children: ReactNode;
  initialSnap?: SnapPointType;
}

const TAB_BAR_HEIGHT = 67;
const MIN_VISIBLE_HEIGHT = 45;
const FOOTER_IDLE_TIMEOUT_MS = 20000;

const ListSheetLeaderboard: React.FC<ListSheetLeaderboardProps> = ({
  children,
  initialSnap = SnapPoint.Full,
}) => {
  const isLandscape = useSelector<IState>(
    (state) => state.app.persistedState.orientationState?.isLandscape
  ) as boolean;
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [snap, setSnap] = useState<SnapPointType>(initialSnap);
  const [isFooterVisible, setIsFooterVisible] = useState(true);
  const previousScrollTopRef = useRef(0);
  const footerIdleTimerRef = useRef<number | null>(null);

  const emitFooterVisibility = useCallback((visible: boolean) => {
    setIsFooterVisible(visible);
    window.dispatchEvent(
      new CustomEvent<FooterVisibilityEventDetail>(FOOTER_VISIBILITY_EVENT, {
        detail: { visible },
      })
    );
  }, []);

  const resetFooterIdleTimer = useCallback(() => {
    if (footerIdleTimerRef.current) {
      window.clearTimeout(footerIdleTimerRef.current);
    }
    footerIdleTimerRef.current = window.setTimeout(() => {
      emitFooterVisibility(true);
    }, FOOTER_IDLE_TIMEOUT_MS);
  }, [emitFooterVisibility]);

  const handleDrawerPanelScroll = useCallback(
    (scrollTop: number) => {
      const currentScrollTop = Math.max(0, scrollTop);
      const delta = currentScrollTop - previousScrollTopRef.current;

      if (delta > 0 && isFooterVisible) {
        emitFooterVisibility(false);
      } else if (delta < 0 && !isFooterVisible) {
        emitFooterVisibility(true);
      }

      previousScrollTopRef.current = currentScrollTop;
      resetFooterIdleTimer();
    },
    [emitFooterVisibility, isFooterVisible, resetFooterIdleTimer]
  );

  // Start idle timer on mount, restore footer on unmount
  useEffect(() => {
    resetFooterIdleTimer();
    return () => {
      if (footerIdleTimerRef.current) {
        window.clearTimeout(footerIdleTimerRef.current);
      }
      emitFooterVisibility(true);
    };
  }, [emitFooterVisibility, resetFooterIdleTimer]);

  useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return (
    <div className={`leaderboard-sheet-wrap leaderboard-sheet-wrap--${snap}`}>
      <ListSheet
        initialSnap={initialSnap}
        onSnapChange={setSnap}
        minHeight={screenHeight - (TAB_BAR_HEIGHT + MIN_VISIBLE_HEIGHT)}
        midHeight={screenHeight * 0.48}
        maxHeight={screenHeight * 0.091}
        totalReportCount={0}
        groupGameName=""
        isBankFilter={false}
        isShowResultToolBar={false}
        isLandscape={isLandscape}
        insightKey=""
        orderBy={OrderByEnum.Desc}
        timeframeKey=""
        isFooterVisible={isFooterVisible}
        onScrollContent={handleDrawerPanelScroll}
      >
        <div className={`leaderboard-sheet leaderboard-sheet--${snap}`}>{children}</div>
      </ListSheet>
    </div>
  );
};

export default ListSheetLeaderboard;
